<template>
  <div class="timeline-heatmap" data-testid="timeline-heatmap">
    <div class="timeline-heatmap__container">
      <!-- センサー名列（固定） -->
      <div class="timeline-heatmap__labels">
        <div class="timeline-heatmap__header-spacer"></div>
        <div
          v-for="sensor in sensors"
          :key="sensor.id"
          class="timeline-heatmap__sensor-name"
          :title="sensor.name"
          data-testid="sensor-name"
        >
          {{ sensor.name }}
        </div>
      </div>

      <!-- グリッド本体（横スクロールは親の枠が担う） -->
      <div class="timeline-heatmap__grid-wrapper">
        <!-- 時間ラベル行 -->
        <div class="timeline-heatmap__time-row">
          <div
            v-for="slot in timeSlots"
            :key="slot.index"
            :class="slot.index % slotsPerHour === 0
              ? 'timeline-heatmap__time-label'
              : 'timeline-heatmap__time-label-hidden'"
            data-testid="time-slot-label"
            :style="{ width: slot.index % slotsPerHour === 0
              ? `${cellWidth * slotsPerHour}px`
              : undefined }"
          >
            <template v-if="slot.index % slotsPerHour === 0">
              {{ formatHourLabel(slot.startMinute) }}
            </template>
          </div>
        </div>

        <!-- センサー行 -->
        <div
          v-for="sensor in sensors"
          :key="sensor.id"
          class="timeline-heatmap__row"
          data-testid="sensor-row"
        >
          <div
            v-for="slot in timeSlots"
            :key="sensor.id + '-' + slot.index"
            class="timeline-heatmap__cell"
            :class="[cellClass(sensor.id, slot.index),
                     { 'cell--hour-boundary': slot.index % slotsPerHour === 0 },
                     { 'cell--clickable': clickable }]"
            :style="{ width: `${cellWidth}px` }"
            :data-testid="`cell-${sensor.id}-${slot.index}`"
            @mouseenter="onCellEnter(sensor.id, slot.index, $event)"
            @mouseleave="onCellLeave"
          ></div>
        </div>
      </div>
    </div>

    <!-- 凡例（複数並べたときに重複させないため、親が最後のセクションにだけ出させる） -->
    <div v-if="showLegend" class="timeline-heatmap__legend" data-testid="heatmap-legend">
      <span>{{ $t('legend.less') }}</span>
      <div class="timeline-heatmap__cell cell--level-0 legend-cell"></div>
      <div class="timeline-heatmap__cell cell--level-1 legend-cell"></div>
      <div class="timeline-heatmap__cell cell--level-2 legend-cell"></div>
      <div class="timeline-heatmap__cell cell--level-3 legend-cell"></div>
      <div class="timeline-heatmap__cell cell--level-4 legend-cell"></div>
      <span>{{ $t('legend.more') }}</span>
    </div>
  </div>
</template>

<script>
import { buildTimeSlots, aggregateEvents, getIntensityLevel } from './timeSlotAggregator';

const CELL_WIDTH_MAP = { 15: 16, 30: 32, 60: 64 };

export default {
  name: 'timeline-heatmap',
  inheritAttrs: false,
  props: {
    sensors: { type: Array, required: true },
    minutes: { type: Number, required: true },
    events: { type: Array, required: true },
    clickable: { type: Boolean, default: true },
    showLegend: { type: Boolean, default: true },
  },
  computed: {
    timeSlots() {
      return buildTimeSlots(this.minutes);
    },
    cellWidth() {
      return CELL_WIDTH_MAP[this.minutes] || 16;
    },
    slotsPerHour() {
      return 60 / this.minutes;
    },
    eventsBySensor() {
      const map = {};
      this.events.forEach((event) => {
        const key = event.camera_id;
        if (!map[key]) map[key] = [];
        map[key].push(event);
      });
      return map;
    },
    eventsBySlot() {
      const result = {};
      this.sensors.forEach((sensor) => {
        const sensorEvents = this.eventsBySensor[sensor.id] || [];
        result[sensor.id] = aggregateEvents(sensorEvents, this.minutes);
      });
      return result;
    },
    maxCount() {
      return Object.values(this.eventsBySlot).reduce((max, slots) => (
        slots.reduce((m, slot) => Math.max(m, slot.length), max)
      ), 0);
    },
  },
  methods: {
    cellClass(sensorId, slotIndex) {
      const slots = this.eventsBySlot[sensorId];
      if (!slots) return 'cell--level-0';
      const count = slots[slotIndex]?.length || 0;
      const level = getIntensityLevel(count, this.maxCount);
      return `cell--level-${level}`;
    },
    onCellEnter(sensorId, slotIndex, event) {
      const rect = event.target.getBoundingClientRect();
      const slots = this.eventsBySlot[sensorId];
      const slotEvents = slots?.[slotIndex] || [];
      const timeLabel = this.timeSlots[slotIndex]?.label || '';
      this.$emit('cell-hover', { sensorId, timeLabel, events: slotEvents, rect });
    },
    onCellLeave() {
      this.$emit('cell-leave');
    },
    formatHourLabel(startMinute) {
      const hour = Math.floor(startMinute / 60);
      return `${String(hour).padStart(2, '0')}:00`;
    },
  },
};
</script>

<i18n lang="yaml">
ja:
  legend:
    less: '少'
    more: '多'
en:
  legend:
    less: 'Less'
    more: 'More'
</i18n>

<style scoped>
.timeline-heatmap__container {
  display: flex;
}

.timeline-heatmap__labels {
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 1;
  /* 横スクロール時にマス目が背後を通るため、透けないよう地色で塗る */
  background: #fff;
}

.timeline-heatmap__header-spacer {
  height: 28px;
}

.timeline-heatmap__sensor-name {
  height: 36px;
  line-height: 36px;
  padding-right: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* 種別ごとに表を分けても左右の列が揃うよう、内容に依らず固定幅にする */
  width: 140px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.timeline-heatmap__sensor-name[title] {
  cursor: default;
}

/* 横スクロール枠は親が 1 つだけ持つ。ここには置かない */
.timeline-heatmap__grid-wrapper {
  flex: 1;
}

.timeline-heatmap__time-row {
  display: flex;
  height: 28px;
  align-items: flex-end;
  padding-bottom: 4px;
}

.timeline-heatmap__time-label {
  font-size: 11px;
  text-align: left;
  color: #888;
  flex-shrink: 0;
}

.timeline-heatmap__time-label-hidden {
  width: 0;
  overflow: hidden;
}

.timeline-heatmap__row {
  display: flex;
  height: 36px;
  align-items: center;
}

.timeline-heatmap__cell {
  height: 28px;
  flex-shrink: 0;
  border: 0.5px solid #e8e8e8;
  transition: opacity 0.1s;
}

.timeline-heatmap__cell:hover {
  opacity: 0.7;
}

.cell--clickable {
  cursor: pointer;
}

.cell--hour-boundary {
  border-left: 1.5px solid #bbb;
}

.cell--level-0 {
  background-color: #f0f0f0;
}

.cell--level-1 {
  background-color: #c8dff0;
}

.cell--level-2 {
  background-color: #8bbcde;
}

.cell--level-3 {
  background-color: #4d98cc;
}

.cell--level-4 {
  background-color: #0068b6;
}

.timeline-heatmap__legend {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 16px;
  font-size: 12px;
  color: #888;
  /* 親の横スクロール枠の中にあるため、左端に留める */
  position: sticky;
  left: 0;
  width: fit-content;
}

.legend-cell {
  width: 16px;
  height: 16px;
  border: 0.5px solid #ddd;
}
</style>
